import getIn from 'immutable-less/transmute/getIn';
import partial from 'transmute/partial';
import { isEventBasedWorkflow } from 'automation-ui-enrollment/utils/EnrollmentDeciders';
import ActionPreview from 'automation-ui-canvas/components/test-flow/ActionPreview';
import EnrollmentActionPreview from 'automation-ui-canvas/components/test-flow/EnrollmentActionPreview';
import ExtensionActionPreview from 'automation-ui-canvas/components/test-flow/ExtensionActionPreview';
import BranchActionPreview from 'automation-ui-canvas/components/test-flow/BranchActionPreview';
import TimestampPreview from 'automation-ui-canvas/components/test-flow/TimestampPreview';
import { LogEventTypes, ExecutionStates } from 'automation-ui-log-events/constants/LogEventConstants';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { useAllExtensionDefinitions } from 'automation-ui-action-features/actions/extension/extensionQueries';
import { getActionHeaderTitle, getActionOutputsForTargetActionById, getClassicRecurAnnually } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import SkippedPreview from 'automation-ui-canvas/components/test-flow/SkippedPreview';
import EmailActionPreviewAlert from 'automation-ui-canvas/components/alerts/EmailActionPreviewAlert';
import { useActionOutputs } from 'automation-ui-action-features/action-outputs/actionOutputHooks';
import { useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SimulatedActionExecutionEvent = ({
  event,
  deferredEventIds = {},
  objectId,
  flowObjectTypeId,
  flow,
  options,
  enrollmentOverride,
  branchIndexOverrides,
  handleBranchIndexOverrideChange,
  simulatedEnrollmentResult,
  handleEnrollmentOverrideChange,
  hideHeader
}) => {
  var _flow$createMetadata, _allExtensionsData$al;
  const isUngated = useGetIsUngated();
  const {
    type,
    executionState,
    contextMap
  } = event || {};
  const {
    data: allExtensionsData
  } = useAllExtensionDefinitions({
    objectType: flowObjectTypeId,
    createdSourceApp: (_flow$createMetadata = flow.createMetadata) === null || _flow$createMetadata === void 0 ? void 0 : _flow$createMetadata.sourceApp
  });
  const extensionDefinitions = (_allExtensionsData$al = allExtensionsData === null || allExtensionsData === void 0 ? void 0 : allExtensionsData.allExtensionDefinitions) !== null && _allExtensionsData$al !== void 0 ? _allExtensionsData$al : {};
  const {
    instanceOutputsByActionId,
    allOutputsByActionTypeId
  } = useActionOutputs(flow);
  if (type === LogEventTypes.ACTION_EXECUTION) {
    const actionId = getIn(['action', 'actionId'], event);
    const actionType = getIn(['action', 'actionType'], event);
    const action = getIn(['action'], event);
    switch (executionState) {
      case ExecutionStates.DEFERRED:
        deferredEventIds[actionId] = true;
        break;
      case ExecutionStates.ASYNC:
        deferredEventIds[actionId] = true;
        break;
      case ExecutionStates.SUCCESS:
        if (deferredEventIds[actionId]) {
          return null;
        }
        break;
      case ExecutionStates.FAIL_RESCHEDULE:
        // If the flow has business hours (time windows) set, and the test is run outside of
        // those hours, the first action will fail with:
        //   "executionState": "FAIL_RESCHEDULE",
        //   "errorType": "FLOW_OUTSIDE_TIME_WINDOW",
        // The start time (beginning of the next valid time window) is represented by:
        //   "contextMap": {
        //     "DEFERRED_TIMESTAMP": 1548511204394
        //   }
        return /*#__PURE__*/_jsx(TimestampPreview, {
          timestamp: contextMap.DEFERRED_TIMESTAMP,
          use: "rescheduled"
        });
      default:
        // Some actions can fail (such as EMAIL when sendEmailsToUser=true and the
        // object has no associated contacts). Show the actions anyway.
        break;
    }
    const getHeaderTitle = getActionHeaderTitle({
      flow,
      extensionDefinitions,
      isUngated
    });
    const handleGetActionOutputsForTargetActionById = (targetActionId, isNew = false) => getActionOutputsForTargetActionById({
      flow,
      allOutputsByActionTypeId,
      instanceOutputsByActionId,
      targetActionId,
      isNewAction: isNew
    });
    const actionLabel = !hideHeader && getHeaderTitle(event.action);
    switch (actionType) {
      case ActionTypes.LIST_BRANCH:
      case ActionTypes.AB_TEST_BRANCH:
      case ActionTypes.STATIC_BRANCH:
        return /*#__PURE__*/_jsx(BranchActionPreview, {
          logEvent: event,
          flowObjectTypeId: flowObjectTypeId,
          branchIndexOverride: branchIndexOverrides && branchIndexOverrides.get(actionId),
          onBranchIndexOverrideChange: handleBranchIndexOverrideChange ? partial(handleBranchIndexOverrideChange, actionId) : () => {},
          actionLabel: actionLabel,
          getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
        });
      case ActionTypes.AUTOMATION_EXTENSION:
        return /*#__PURE__*/_jsx(ExtensionActionPreview, {
          action: action,
          actionLabel: actionLabel,
          flowObjectTypeId: flowObjectTypeId,
          getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById,
          getHeaderTitle: getHeaderTitle,
          logEvent: event,
          renderContentOnly: !actionLabel
        });
      case ActionTypes.EMAIL:
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ActionPreview, {
            logEvent: event,
            flowObjectTypeId: flowObjectTypeId,
            flow: flow,
            actionLabel: actionLabel,
            renderContentOnly: !actionLabel,
            extensionDefinitions: extensionDefinitions,
            getHeaderTitle: getHeaderTitle,
            getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
          }), executionState === ExecutionStates.SKIPPED ? /*#__PURE__*/_jsx(SkippedPreview, {
            recursAnnually: getClassicRecurAnnually(flow)
          }) : null, options !== null && options !== void 0 && options.sendEmailsToUser ? /*#__PURE__*/_jsx(EmailActionPreviewAlert, {
            logEvent: event,
            objectId: objectId,
            flowObjectTypeId: flowObjectTypeId
          }) : null]
        });
      default:
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ActionPreview, {
            logEvent: event,
            flowObjectTypeId: flowObjectTypeId,
            flow: flow,
            actionLabel: actionLabel,
            renderContentOnly: !actionLabel,
            extensionDefinitions: extensionDefinitions,
            getHeaderTitle: getHeaderTitle,
            getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
          }), executionState === ExecutionStates.SKIPPED ? /*#__PURE__*/_jsx(SkippedPreview, {
            recursAnnually: getClassicRecurAnnually(flow)
          }) : null]
        });
    }
  } else if (type === LogEventTypes.ENROLLMENT) {
    return /*#__PURE__*/_jsx(EnrollmentActionPreview, {
      isEventTriggers: isEventBasedWorkflow(flow.toHybridFlowInterface()),
      simulatedEnrollmentResult: simulatedEnrollmentResult,
      flowObjectTypeId: flowObjectTypeId,
      onEnrollmentOverrideChange: handleEnrollmentOverrideChange || (__value => {}),
      overridden: !!enrollmentOverride,
      objectId: objectId,
      logEvent: event
    });
  }
  return null;
};
export default SimulatedActionExecutionEvent;